import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/pages/layoutChrome";
import BuyerRequestsDesignerPanel from "@/components/users/BuyerRequestsDesignerPanel";
import PlatformLicenseRequests from "@/pages/platform/PlatformLicenseRequests";
import { CONFIGURACION_EMISOR_PATH } from "@/modules/config/configuracionRoutes";
import { CLASSIFICATION_NOTICES } from "@/modules/cardClassificationRegistry";
import { useAuth } from "@/lib/auth";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { useCapabilities } from "@/lib/useCapabilities";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Seguimiento comercial de solicitudes comprador.
 * Bandeja con acciones (revisar / aprobar / rechazar) vía GET/PATCH /platform/license-requests.
 * Seguimiento de cuentas trial vía GET /admin/users.
 */
export default function DesignerBuyerRequestsPage() {
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const { loading: capabilitiesLoading, permissions, isPlatformDesigner: capsPlatformDesigner } =
    useCapabilities();
  const canUse = Boolean(user?.can_generate_licenses);
  const canReviewLicenseRequests = Boolean(
    isPlatformDesigner || capsPlatformDesigner || permissions?.access_license_center,
  );

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canUse) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/admin/users")
      .then(({ data }) => {
        if (!cancelled) setUsers(Array.isArray(data) ? data : data?.items || []);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar usuarios");
          setUsers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canUse]);

  if (!canUse) {
    const fallback = isPlatformDesigner ? "/disenador/inicio" : "/";
    return <Navigate to={fallback} replace />;
  }

  return (
    <>
      <PageHeader
        title="Solicitudes de compradores"
        subtitle="Revisa solicitudes formales de licencia y el seguimiento de cuentas en trial o pendientes de activación."
        breadcrumb={["Módulo Maestro Diseñador", "Solicitudes"]}
      />
      <div className="p-8 max-w-7xl space-y-6" data-testid="designer-buyer-requests-page">
        <p className="text-sm text-slate-600 m-0">{CLASSIFICATION_NOTICES.buyerCatalog}</p>
        <div className="flex flex-wrap gap-2 text-xs">
          <Link to="/catalogo-compradores" className="text-[#0F2D5C] underline font-medium">
            Catálogo de compradores
          </Link>
          <Link to="/cartera-licencias" className="text-[#0F2D5C] underline font-medium">
            Cartera de licencias
          </Link>
          <Link to={CONFIGURACION_EMISOR_PATH} className="text-[#0F2D5C] underline font-medium">
            Gestor de licencias
          </Link>
          {canReviewLicenseRequests ? (
            <Link to="/platform/license-requests" className="text-[#0F2D5C] underline font-medium">
              Centro SaaS — solicitudes
            </Link>
          ) : null}
        </div>
        {!capabilitiesLoading && canReviewLicenseRequests ? (
          <PlatformLicenseRequests embedded />
        ) : null}
        {loading ? (
          <div className="flex justify-center py-12 text-slate-500">
            <Loader2 className="w-7 h-7 animate-spin" />
          </div>
        ) : (
          <BuyerRequestsDesignerPanel users={users} showAccountTrackingTitle />
        )}
      </div>
    </>
  );
}
